import { Box, Divider, Popover } from '@material-ui/core';
import { FormProvider, useForm } from 'react-hook-form';

import { CustomDate } from '@/components/modules/Metrics/shared/entities/CustomDate';
import { TimeInterval } from '@/components/modules/Metrics/shared/entities/TimeInterval';
import { DateRange } from '@/components/modules/Metrics/shared/types/DateRange';
import { TimeIntervalType } from '@/components/modules/Metrics/shared/types/TimeIntervalType';
import { Font } from '@/components/shared/components/Font';
import { DateFormField } from '@/components/shared/components/NewContextForms/DateFormField';
import { FieldVariant } from '@/components/shared/components/NewContextForms/types/FieldVariant';
import { StandardButton } from '@/components/shared/components/StandardButton';
import { Text } from '@/components/shared/components/Text';

interface CustomTimeIntervalPopoverProps {
  anchorEl: HTMLElement | null;
  timeInterval: TimeInterval;
  onClose: () => void;
  setTimeInterval: React.Dispatch<React.SetStateAction<TimeInterval>>;
}

export const CustomTimeIntervalPopover = ({
  anchorEl,
  timeInterval,
  onClose,
  setTimeInterval,
}: CustomTimeIntervalPopoverProps) => {
  const form = useForm<DateRange>({ defaultValues: timeInterval.current });
  const { start, end } = form.watch();

  const today = new CustomDate().date;
  const isValid = !!start && !!end && start <= end && end <= today;
  const preview = isValid ? new TimeInterval(TimeIntervalType.CUSTOM, { start, end }) : undefined;

  return (
    <Popover
      open={!!anchorEl}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      PaperProps={{ style: { width: 400, marginTop: 8, paddingBlock: 16 } }}
    >
      <FormProvider {...form}>
        <Box px={3} pb={2}>
          <Font variant="h5" color="title">
            <strong>
              <Text tId="METRICS_PERIOD_CUSTOM_TITLE" />
            </strong>
          </Font>
          <Font variant="caption" color="textSecondary">
            {preview ? (
              <Text tId="METRICS_COMPARISON_PREVIOUS_DAYS" dynamicDataTexts={[String(preview.days)]} />
            ) : (
              <Text tId="METRICS_PERIOD_CUSTOM_INVALID" />
            )}
          </Font>
        </Box>
        <Box px={3} pb={2} display="grid" gridTemplateColumns="1fr 1fr" gridGap={16}>
          <DateFormField<DateRange>
            name="start"
            label="METRICS_PERIOD_FROM"
            fieldVariant={FieldVariant.form}
            maxDate={end ?? today}
            keepTimezone
          />
          <DateFormField<DateRange>
            name="end"
            label="METRICS_PERIOD_TO"
            fieldVariant={FieldVariant.form}
            minDate={start ?? undefined}
            maxDate={today}
            keepTimezone
          />
        </Box>
        <Divider />
        <Box px={3} pt={2} display="flex" justifyContent="flex-end" gridGap={8}>
          <StandardButton size="small" onClick={onClose}>
            <Text tId="CANCEL" />
          </StandardButton>
          <StandardButton
            main
            size="small"
            disabled={!preview}
            onClick={() => {
              if (!preview) return;
              setTimeInterval(preview);
              onClose();
            }}
          >
            <Text tId="METRICS_PERIOD_APPLY" />
          </StandardButton>
        </Box>
      </FormProvider>
    </Popover>
  );
};
